import React, { useRef, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import { useMap } from './MapContext';
import { useOgcServices } from '../services/OgcServicesContext';
import { getAuthHeader } from '../services/AuthContext';
import {
  GPKG_VECTOR_TILE_AIRPORTS_SOURCE_LAYER,
  GPKG_VECTOR_TILE_NAVAIDS_SOURCE_LAYER,
  GPKG_VECTOR_TILE_OBSTACLES_SOURCE_LAYER,
  GPKG_VECTOR_TILE_AIRWAYS_SOURCE_LAYER,
} from '../config/environment';
import {
  FIR_BOUNDARIES_SOURCE_ID,
  FIXES_SOURCE_ID,
  getAllAirspaceStyles,
  createAirwayLayer,
  createAirspaceFillLayer,
  createAirspaceLineLayer,
  createAirportSymbolLayer,
  createFirBoundaryLayer,
  createFixLabelLayer,
  createFixLayer,
  createObstacleLayer,
  createNavaidLayer,
  createRasterChartLayer,
} from './aviation-sources';
import {
  WEATHER_SOURCE_IDS,
  createAirmetLayer,
  createNexradLayer,
  createPirepLayer,
  createSigmetLayer,
} from './weather-sources';
import { applyMapSettings, loadMapSettings, TERRAIN_SOURCE_ID } from '../services/map-settings';
import { applyOverlayState, getSavedOverlayStateSync } from '../services/overlay-settings';

/** Default center: center of CONUS */
const DEFAULT_CENTER: [number, number] = [-98.5, 39.8];
const DEFAULT_ZOOM = 4.5;
const EMPTY_FEATURE_COLLECTION = { type: 'FeatureCollection' as const, features: [] };

const RASTER_CHART_IDS: Array<{ id: string; source: string }> = [
  { id: 'vfr-sectional', source: 'vfr-sectional-raster' },
  { id: 'tac',           source: 'tac-raster' },
  { id: 'ifr-low',       source: 'ifr-low-raster' },
  { id: 'ifr-high',      source: 'ifr-high-raster' },
  { id: 'ifr-area',      source: 'ifr-area-raster' },
  { id: 'heli-route',    source: 'heli-route-raster' },
];

export const MapView: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { map, setMap } = useMap();
  const { tilesClient } = useOgcServices();

  useEffect(() => {
    if (!containerRef.current || map) return;

    // Build the vector + raster sources from the configured endpoints
    const chartSources = tilesClient.getAllChartSources();
    const vectorSources = tilesClient.getAllVectorSources();

    const vectorStyleSources: Record<string, maplibregl.SourceSpecification> = {
      ...(vectorSources['airspaces-vt'] ? { 'airspaces-vt': vectorSources['airspaces-vt'] } : {}),
      ...(vectorSources['airports-vt'] ? { 'airports-vt': vectorSources['airports-vt'] } : {}),
      ...(vectorSources['obstacles-vt'] ? { 'obstacles-vt': vectorSources['obstacles-vt'] } : {}),
      ...(vectorSources['navaids-vt'] ? { 'navaids-vt': vectorSources['navaids-vt'] } : {}),
      ...(vectorSources['airways-vt'] ? { 'airways-vt': vectorSources['airways-vt'] } : {}),
    };

    const m = new maplibregl.Map({
      container: containerRef.current,
      // Inject bearer token for every tile / feature request served by
      // our own backends. External hosts (OSM, ESRI) are left alone.
      transformRequest: (url) => {
        const isOurs =
          url.startsWith('/') ||
          url.includes('/api/gpkg/') ||
          url.includes('/api/ogc/') ||
          url.includes('/api/charts/') ||
          url.includes('/api/stac/') ||
          url.includes('/api/overlays/') ||
          url.includes('/tiles/');
        return isOurs ? { url, headers: getAuthHeader() } : { url };
      },
      style: {
        version: 8,
        name: 'EFB Aviation',
        sources: {
          'osm-raster': {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '© OpenStreetMap contributors',
          },
          // Raster aviation chart sources
          'vfr-sectional-raster': chartSources['vfr-sectional'],
          'tac-raster':           chartSources['tac'],
          'ifr-low-raster':       chartSources['ifr-low'],
          'ifr-high-raster':      chartSources['ifr-high'],
          'ifr-area-raster':      chartSources['ifr-area'],
          'heli-route-raster':    chartSources['heli-route'],
          [TERRAIN_SOURCE_ID]:    tilesClient.getDemSource(),
          [WEATHER_SOURCE_IDS.nexrad]:  tilesClient.getRasterSource('nexrad', { maxzoom: 8 }),
          [WEATHER_SOURCE_IDS.sigmets]: { type: 'geojson', data: EMPTY_FEATURE_COLLECTION },
          [WEATHER_SOURCE_IDS.airmets]: { type: 'geojson', data: EMPTY_FEATURE_COLLECTION },
          [WEATHER_SOURCE_IDS.pireps]:  { type: 'geojson', data: EMPTY_FEATURE_COLLECTION },
          [FIXES_SOURCE_ID]:            { type: 'geojson', data: EMPTY_FEATURE_COLLECTION },
          [FIR_BOUNDARIES_SOURCE_ID]:   { type: 'geojson', data: EMPTY_FEATURE_COLLECTION },
          // Vector tile feature sources
          ...vectorStyleSources,
        },
        layers: [
          { id: 'osm-base', type: 'raster', source: 'osm-raster' },
        ],
        glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      },
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      maxZoom: 18,
      attributionControl: false,
      hash: true,
    });

    m.addControl(new maplibregl.NavigationControl(), 'bottom-right');
    m.addControl(new maplibregl.ScaleControl({ unit: 'nautical' }), 'bottom-left');
    m.addControl(new maplibregl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
    }), 'bottom-right');

    m.on('error', (event) => {
      const error = event.error as Error | undefined;
      if (error?.message) {
        console.warn('[efb] map error:', error.message);
      }
    });

    m.on('load', () => {
      // Raster chart layers (all hidden by default — BasemapPanel toggles)
      for (const { id, source } of RASTER_CHART_IDS) {
        try {
          m.addLayer(createRasterChartLayer(id, source, 0.85, false));
        } catch (e) { console.warn('[efb] raster chart layer failed:', id, e); }
      }

      // Weather overlays. The panel controls visibility and refreshes advisory
      // GeoJSON for the current viewport from AviationWeather.gov.
      try { m.addLayer(createNexradLayer(WEATHER_SOURCE_IDS.nexrad)); } catch (e) { console.warn('[efb] NEXRAD layer failed', e); }
      try { m.addLayer(createSigmetLayer(WEATHER_SOURCE_IDS.sigmets)); } catch (e) { console.warn('[efb] SIGMET layer failed', e); }
      try { m.addLayer(createAirmetLayer(WEATHER_SOURCE_IDS.airmets)); } catch (e) { console.warn('[efb] AIRMET layer failed', e); }
      try { m.addLayer(createPirepLayer(WEATHER_SOURCE_IDS.pireps)); } catch (e) { console.warn('[efb] PIREP layer failed', e); }

      // Airspace vector layers (fill + line per class/type)
      if (m.getSource('airspaces-vt')) {
        for (const def of getAllAirspaceStyles()) {
          try {
            m.addLayer(createAirspaceFillLayer(def));
            m.addLayer(createAirspaceLineLayer(def));
          } catch (e) { console.warn('[efb] airspace layer failed:', def.id, e); }
        }
      }

      // Point feature layers
      if (m.getSource('airports-vt')) {
        try { m.addLayer(createAirportSymbolLayer('airports-vt', GPKG_VECTOR_TILE_AIRPORTS_SOURCE_LAYER)); } catch (e) { console.warn('[efb] airports layer failed', e); }
      }
      if (m.getSource('obstacles-vt')) {
        try { m.addLayer(createObstacleLayer('obstacles-vt', GPKG_VECTOR_TILE_OBSTACLES_SOURCE_LAYER)); } catch (e) { console.warn('[efb] obstacles layer failed', e); }
      }
      if (m.getSource('navaids-vt')) {
        try { m.addLayer(createNavaidLayer('navaids-vt', GPKG_VECTOR_TILE_NAVAIDS_SOURCE_LAYER)); } catch (e) { console.warn('[efb] navaids layer failed', e); }
      }
      if (m.getSource('airways-vt')) {
        try { m.addLayer(createAirwayLayer('airways-vt', GPKG_VECTOR_TILE_AIRWAYS_SOURCE_LAYER)); } catch (e) { console.warn('[efb] airways layer failed', e); }
      }
      try { m.addLayer(createFixLayer()); } catch (e) { console.warn('[efb] fixes layer failed', e); }
      try { m.addLayer(createFixLabelLayer()); } catch (e) { console.warn('[efb] fixes label layer failed', e); }
      try { m.addLayer(createFirBoundaryLayer()); } catch (e) { console.warn('[efb] FIR/UIR layer failed', e); }

      applyMapSettings(m, loadMapSettings());
      applyOverlayState(m, getSavedOverlayStateSync());
      setMap(m);
    });

    return () => { m.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%' }}
    />
  );
};
